import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Skeleton,
  SkeletonItem,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  FluentProvider,
  Input,
  SearchBox,
  Select,
  webLightTheme,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowDownload20Regular,
  Book20Regular,
  BuildingBank20Regular,
  Box20Regular,
  Calculator20Regular,
  Cart20Regular,
  Checkmark20Regular,
  ChevronDown12Regular,
  ChevronUp12Regular,
  DataBarVertical20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Dismiss24Regular,
  Edit20Regular,
  Home20Regular,
  Money20Regular,
  Navigation20Regular,
  Payment20Regular,
  Person20Regular,
  Receipt20Regular,
  ShoppingBag20Regular,
  Wallet20Regular,
  ArrowSwap20Regular,
  ArrowUndo20Regular,
  Beaker20Regular,
  Briefcase20Regular,
  Building20Regular,
  BuildingMultiple20Regular,
  Calendar20Regular,
  ClipboardTaskListLtr20Regular,
  Clock20Regular,
  DataPie20Regular,
  Document20Regular,
  DocumentBulletList20Regular,
  Flow20Regular,
  Globe20Regular,
  History20Regular,
  LockClosed20Regular,
  Organization20Regular,
  People20Regular,
  Shield20Regular,
  Table20Regular,
  Tag20Regular,
  TagMultiple20Regular,
  Wrench20Regular,
} from "@fluentui/react-icons";
import styles from "./FinanceAndOperation.module.css";
import gp from "./Genericpage.module.css";
import GenericPage from "./Genericpage";
import { BINDINGS, loadCtx, type Ctx } from "./Ops";
import { BASE, errText, getToken } from "./Api/Api";

/* =====================================================================
   Types
   ===================================================================== */
type Val = string | number;
interface Row {
  id: string;
  [k: string]: Val;
}
type Data = Record<string, Row[]>;

interface Opt {
  v: string;
  l: string;
}
interface Fld {
  key: string;
  label: string;
  kind: "text" | "number" | "date" | "select";
  options?: Opt[] | ((d: Data) => Opt[]);
  required?: boolean;
  full?: boolean;
}
interface Col {
  key: string;
  label: string;
  kind?: "money" | "date" | "status" | "num" | "pct" | "mono";
  get?: (r: Row, d: Data) => Val;
  right?: boolean;
}
/** an adjustment to a ledger account's natural balance */
interface Fx {
  gl: string;
  d: number;
}
interface Res {
  patch?: Record<string, Val>;
  fx?: Fx[];
  /** movement on the main operating bank account (also posts to GL 1010) */
  bank?: number;
  msg: string;
  err?: string;
  /** move money between specific bank accounts (by id) */
  bankMoves?: { id: string; d: number }[];
  /** patch records on other pages */
  other?: { ent: string; id: string; patch: Record<string, Val> }[];
  /** create a record on another page */
  create?: { ent: string; values: Record<string, Val> };
}
interface Act {
  label: string;
  icon: React.ReactNode;
  show: (r: Row) => boolean;
  run: (r: Row, d: Data) => Res;
}
interface Entity {
  id: string;
  group: string;
  title: string;
  subtitle: string;
  noun: string;
  icon: React.ReactNode;
  page: "grid" | "dash" | "trial" | "pnl" | "bs";
  readOnly?: boolean;
  prefix?: string;
  idKey?: string;
  initial?: Record<string, Val>;
  cols: Col[];
  fields: Fld[];
  acts: Act[];
  /** statuses in which a record may still be edited or deleted; omitted = always */
  editable?: string[];
  validate?: (v: Record<string, Val>) => string | null;
}

/* =====================================================================
   Helpers
   ===================================================================== */
const NGN = new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 });
const money = (n: number) => NGN.format(n);
const pad = (n: number) => String(n).padStart(2, "0");
const iso = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
const off = (n: number) => {
  const dt = new Date();
  dt.setDate(dt.getDate() + n);
  return iso(dt);
};
const fdate = (s: string) => {
  if (!s) return "";
  const [y, m, dd] = s.split("-").map(Number);
  return new Date(y || 0, (m || 1) - 1, dd || 1).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const num = (v: Val | undefined): number => (typeof v === "number" ? v : Number(v) || 0);
const rows = (d: Data, k: string): Row[] => d[k] ?? [];
const sum = (list: Row[], k: string) => list.reduce((t, r) => t + num(r[k]), 0);
const R = (id: string, o: Record<string, Val>): Row => ({ id, ...o });
const opts = (...a: string[]): Opt[] => a.map((v) => ({ v, l: v }));

const vendorOpts = (d: Data): Opt[] => rows(d, "vendors").filter((v) => v.status === "Active").map((v) => ({ v: String(v.name), l: String(v.name) }));
const customerOpts = (d: Data): Opt[] => rows(d, "customers").filter((v) => v.status === "Active").map((v) => ({ v: String(v.name), l: String(v.name) }));
const bankOpts = (d: Data): Opt[] => rows(d, "banks").map((b) => ({ v: b.id, l: String(b.name) }));
const itemOpts = (d: Data): Opt[] => rows(d, "inventory").map((i) => ({ v: i.id, l: `${i.id} · ${String(i.name)}` }));
const bankName = (d: Data, id: Val | undefined): string => String(rows(d, "banks").find((b) => b.id === String(id))?.name ?? id ?? "");
/** returns a message when the date falls in a closed ledger period */
const periodErr = (d: Data, date: Val | undefined): string | null => {
  const day = String(date ?? "");
  const p = rows(d, "periods").find((x) => x.status === "Closed" && String(x.start) <= day && day <= String(x.end));
  return p ? `The period ${String(p.name)} is closed. Reopen it in Ledger calendar first.` : null;
};

const debitNormal = (t: string) => t === "Asset" || t === "Expense";
const acctType = (d: Data, a: Val | undefined) => String(rows(d, "coa").find((x) => x.id === String(a))?.type ?? "");
const acctOpts = (type?: string) => (d: Data): Opt[] =>
  rows(d, "coa")
    .filter((a) => !type || a.type === type)
    .map((a) => ({ v: a.id, l: `${a.id} · ${String(a.name)}` }));

const TONE: Record<string, string> = {
  Draft: "grey",
  Posted: "blue",
  Paid: "green",
  Overdue: "red",
  Active: "green",
  "On hold": "amber",
  Confirmed: "blue",
  Received: "green",
  Shipped: "green",
  Cancelled: "grey",
  Submitted: "amber",
  Approved: "blue",
  Rejected: "red",
  Reimbursed: "green",
  "Low stock": "amber",
  "In stock": "green",
  "On track": "green",
  "At risk": "amber",
  "Over budget": "red",
  "Fully depreciated": "grey",
  Open: "green",
  Closed: "grey",
  Inactive: "grey",
  Disabled: "red",
  Enabled: "green",
  Unmatched: "amber",
  Matched: "green",
  "In progress": "blue",
  Completed: "green",
  Sent: "blue",
  Won: "green",
  Lost: "red",
  Converted: "green",
  Credited: "green",
  Picked: "blue",
  Packed: "green",
  Planned: "grey",
  Started: "blue",
  Finished: "green",
  "In transit": "blue",
  Terminated: "red",
};

const val = (c: Col, r: Row, d: Data): Val => (c.get ? c.get(r, d) : r[c.key] ?? "");
const show = (c: Col, v: Val): string => {
  switch (c.kind) {
    case "money":
      return money(num(v));
    case "date":
      return fdate(String(v));
    case "pct":
      return `${num(v)}%`;
    case "num":
      return num(v).toLocaleString("en-NG");
    default:
      return String(v);
  }
};

const Pill: React.FC<{ text: string }> = ({ text }) => <span className={`${styles.pill} ${styles["tone_" + (TONE[text] || "grey")]}`}>{text}</span>;

/** a posted invoice past its due date is shown as Overdue */
const invStatus = (r: Row) => (r.status === "Posted" && String(r.due) < off(0) ? "Overdue" : String(r.status));

const setStatus = (label: string, icon: React.ReactNode, from: string, to: string, msg: (r: Row) => string): Act => ({
  label,
  icon,
  show: (r) => r.status === from,
  run: (r) => ({ patch: { status: to }, msg: msg(r) }),
});

const amountOk = (v: Record<string, Val>) => (num(v.amount) > 0 ? null : "Amount must be greater than zero.");

/* =====================================================================
   Entities (every page of the module is described here)
   ===================================================================== */
const holdActs = (noun: string): Act[] => [
  setStatus("Put on hold", <Dismiss20Regular />, "Active", "On hold", (r) => `${String(r.name)} put on hold`),
  setStatus("Activate", <Checkmark20Regular />, "On hold", "Active", (r) => `${String(r.name)} is active again (${noun})`),
];

const ENTITIES: Entity[] = [
  { id: "dashboard", group: "Workspaces", title: "Finance workspace", subtitle: "Cash, receivables, payables and the work waiting for you.", noun: "", icon: <Home20Regular />, page: "dash", cols: [], fields: [], acts: [] },

  /* ---------------- General ledger ---------------- */
  {
    id: "coa",
    group: "General ledger",
    title: "Chart of accounts",
    subtitle: "Main accounts and their current balances.",
    noun: "account",
    icon: <Book20Regular />,
    page: "grid",
    idKey: "account",
    cols: [
      { key: "account", label: "Main account", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Account type" },
      { key: "balance", label: "Balance", kind: "money", right: true },
    ],
    fields: [
      { key: "account", label: "Main account", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "type", label: "Account type", kind: "select", options: opts("Asset", "Liability", "Equity", "Revenue", "Expense"), required: true },
    ],
    initial: { balance: 0 },
    acts: [],
  },
  {
    id: "journal",
    group: "General ledger",
    title: "Journal entries",
    subtitle: "Create a journal, then post it to update the ledger.",
    noun: "journal",
    icon: <Receipt20Regular />,
    page: "grid",
    prefix: "JNL-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Journal", kind: "mono" },
      { key: "date", label: "Date", kind: "date" },
      { key: "description", label: "Description" },
      { key: "debit", label: "Debit account", kind: "mono" },
      { key: "credit", label: "Credit account", kind: "mono" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
      { key: "debit", label: "Debit account", kind: "select", options: acctOpts(), required: true },
      { key: "credit", label: "Credit account", kind: "select", options: acctOpts(), required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: (v) => (v.debit === v.credit ? "Debit and credit accounts must be different." : amountOk(v)),
    acts: [
      {
        label: "Post",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r, d) => {
          const pe = periodErr(d, r.date);
          if (pe) return { msg: "", err: pe };
          const a = num(r.amount);
          const dt = debitNormal(acctType(d, r.debit));
          const ct = debitNormal(acctType(d, r.credit));
          return {
            patch: { status: "Posted" },
            fx: [
              { gl: String(r.debit), d: dt ? a : -a },
              { gl: String(r.credit), d: ct ? -a : a },
            ],
            msg: `${r.id} posted to the ledger`,
          };
        },
      },
    ],
  },
  { id: "trial", group: "General ledger", title: "Trial balance", subtitle: "Debit and credit balances for every main account.", noun: "", icon: <Calculator20Regular />, page: "trial", cols: [], fields: [], acts: [] },

  /* ---------------- Accounts payable ---------------- */
  {
    id: "vendors",
    group: "Accounts payable",
    title: "All vendors",
    subtitle: "Suppliers and what you currently owe them.",
    noun: "vendor",
    icon: <ShoppingBag20Regular />,
    page: "grid",
    prefix: "V-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "Vendor account", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "group", label: "Vendor group" },
      { key: "terms", label: "Payment terms" },
      { key: "phone", label: "Phone" },
      { key: "owed", label: "Balance", kind: "money", right: true, get: (r, d) => sum(rows(d, "ap_invoices").filter((i) => i.vendor === r.name && i.status === "Posted"), "amount") },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "group", label: "Vendor group", kind: "select", options: opts("Food & beverage", "Utilities", "Maintenance", "Linen & laundry", "Services"), required: true },
      { key: "terms", label: "Payment terms", kind: "select", options: opts("Net 0", "Net 15", "Net 30", "Net 60"), required: true },
      { key: "phone", label: "Phone", kind: "text" },
    ],
    acts: holdActs("vendor"),
  },
  {
    id: "ap_invoices",
    group: "Accounts payable",
    title: "Vendor invoices",
    subtitle: "Post invoices to payables, then pay them from the operating bank account.",
    noun: "vendor invoice",
    icon: <Payment20Regular />,
    page: "grid",
    prefix: "VI-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Invoice", kind: "mono" },
      { key: "vendor", label: "Vendor" },
      { key: "date", label: "Invoice date", kind: "date" },
      { key: "due", label: "Due date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status", get: (r) => invStatus(r) },
    ],
    fields: [
      { key: "vendor", label: "Vendor", kind: "select", options: (d) => rows(d, "vendors").filter((v) => v.status === "Active").map((v) => ({ v: String(v.name), l: String(v.name) })), required: true, full: true },
      { key: "date", label: "Invoice date", kind: "date", required: true },
      { key: "due", label: "Due date", kind: "date", required: true },
      { key: "expense", label: "Expense account", kind: "select", options: acctOpts("Expense"), required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      {
        label: "Post",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r, d) => {
          const pe = periodErr(d, r.date);
          if (pe) return { msg: "", err: pe };
          const a = num(r.amount);
          return { patch: { status: "Posted" }, fx: [{ gl: String(r.expense || "5200"), d: a }, { gl: "2010", d: a }], msg: `${r.id} posted to payables` };
        },
      },
      {
        label: "Pay",
        icon: <Money20Regular />,
        show: (r) => r.status === "Posted",
        run: (r, d) => {
          const a = num(r.amount);
          const bank = rows(d, "banks")[0];
          if (!bank || num(bank.balance) < a) return { msg: "", err: `Insufficient balance in ${String(bank?.name ?? "the operating account")}.` };
          return { patch: { status: "Paid" }, bank: -a, fx: [{ gl: "2010", d: -a }], msg: `${r.id} paid from ${String(bank.name)}` };
        },
      },
    ],
  },

  /* ---------------- Accounts receivable ---------------- */
  {
    id: "customers",
    group: "Accounts receivable",
    title: "All customers",
    subtitle: "Customers, credit limits and what they owe you.",
    noun: "customer",
    icon: <Person20Regular />,
    page: "grid",
    prefix: "C-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "Customer account", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "group", label: "Customer group" },
      { key: "terms", label: "Payment terms" },
      { key: "limit", label: "Credit limit", kind: "money", right: true },
      { key: "owed", label: "Balance", kind: "money", right: true, get: (r, d) => sum(rows(d, "ar_invoices").filter((i) => i.customer === r.name && i.status === "Posted"), "amount") },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "group", label: "Customer group", kind: "select", options: opts("Corporate", "Individual", "Travel agent", "Government"), required: true },
      { key: "terms", label: "Payment terms", kind: "select", options: opts("Net 0", "Net 15", "Net 30", "Net 60"), required: true },
      { key: "limit", label: "Credit limit", kind: "number" },
    ],
    acts: holdActs("customer"),
  },
  {
    id: "ar_invoices",
    group: "Accounts receivable",
    title: "Customer invoices",
    subtitle: "Post invoices to receivables, then record the payment when it arrives.",
    noun: "customer invoice",
    icon: <Receipt20Regular />,
    page: "grid",
    prefix: "INV-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Invoice", kind: "mono" },
      { key: "customer", label: "Customer" },
      { key: "date", label: "Invoice date", kind: "date" },
      { key: "due", label: "Due date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status", get: (r) => invStatus(r) },
    ],
    fields: [
      { key: "customer", label: "Customer", kind: "select", options: (d) => rows(d, "customers").filter((v) => v.status === "Active").map((v) => ({ v: String(v.name), l: String(v.name) })), required: true, full: true },
      { key: "date", label: "Invoice date", kind: "date", required: true },
      { key: "due", label: "Due date", kind: "date", required: true },
      { key: "revenue", label: "Revenue account", kind: "select", options: acctOpts("Revenue"), required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      {
        label: "Post",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r, d) => {
          const pe = periodErr(d, r.date);
          if (pe) return { msg: "", err: pe };
          const a = num(r.amount);
          return { patch: { status: "Posted" }, fx: [{ gl: "1100", d: a }, { gl: String(r.revenue || "4010"), d: a }], msg: `${r.id} posted to receivables` };
        },
      },
      {
        label: "Receive payment",
        icon: <Money20Regular />,
        show: (r) => r.status === "Posted",
        run: (r) => {
          const a = num(r.amount);
          return { patch: { status: "Paid" }, bank: a, fx: [{ gl: "1100", d: -a }], msg: `${money(a)} received for ${r.id}` };
        },
      },
    ],
  },

  /* ---------------- Cash and bank ---------------- */
  {
    id: "banks",
    group: "Cash and bank management",
    title: "Bank accounts",
    subtitle: "Balances move when you pay vendors or receive customer payments.",
    noun: "bank account",
    icon: <BuildingBank20Regular />,
    page: "grid",
    prefix: "BK-",
    initial: { status: "Active", balance: 0 },
    cols: [
      { key: "id", label: "Bank account", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "bank", label: "Bank" },
      { key: "accountNo", label: "Account number", kind: "mono" },
      { key: "currency", label: "Currency" },
      { key: "balance", label: "Balance", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "bank", label: "Bank", kind: "text", required: true },
      { key: "accountNo", label: "Account number", kind: "text", required: true },
      { key: "currency", label: "Currency", kind: "select", options: opts("NGN", "USD"), required: true },
    ],
    acts: holdActs("bank account"),
  },

  /* ---------------- Fixed assets ---------------- */
  {
    id: "assets",
    group: "Fixed assets",
    title: "Fixed assets",
    subtitle: "Asset register with monthly depreciation.",
    noun: "asset",
    icon: <Wallet20Regular />,
    page: "grid",
    prefix: "FA-",
    initial: { accum: 0 },
    cols: [
      { key: "id", label: "Asset", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "group", label: "Group" },
      { key: "acquired", label: "Acquired", kind: "date" },
      { key: "cost", label: "Cost", kind: "money", right: true },
      { key: "accum", label: "Accumulated depreciation", kind: "money", right: true },
      { key: "nbv", label: "Net book value", kind: "money", right: true, get: (r) => num(r.cost) - num(r.accum) },
      { key: "status", label: "Status", kind: "status", get: (r) => (num(r.cost) - num(r.accum) <= 0 ? "Fully depreciated" : "Active") },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "group", label: "Group", kind: "select", options: opts("Buildings", "Furniture & fittings", "Kitchen equipment", "Vehicles", "IT equipment"), required: true },
      { key: "acquired", label: "Acquisition date", kind: "date", required: true },
      { key: "cost", label: "Acquisition cost", kind: "number", required: true },
      { key: "life", label: "Useful life (years)", kind: "number", required: true },
    ],
    validate: (v) => (num(v.cost) <= 0 || num(v.life) <= 0 ? "Cost and useful life must be greater than zero." : null),
    acts: [
      {
        label: "Run depreciation",
        icon: <DataBarVertical20Regular />,
        show: (r) => num(r.cost) - num(r.accum) > 0,
        run: (r) => {
          const monthly = Math.round(num(r.cost) / (num(r.life) * 12));
          const dep = Math.min(monthly, num(r.cost) - num(r.accum));
          return { patch: { accum: num(r.accum) + dep }, fx: [{ gl: "5300", d: dep }, { gl: "1590", d: -dep }], msg: `${money(dep)} depreciation posted for ${String(r.name)}` };
        },
      },
    ],
  },

  /* ---------------- Budgeting ---------------- */
  {
    id: "budgets",
    group: "Budgeting",
    title: "Budget vs actual",
    subtitle: "Track spend against the approved budget by department.",
    noun: "budget line",
    icon: <DataBarVertical20Regular />,
    page: "grid",
    prefix: "BG-",
    cols: [
      { key: "id", label: "Line", kind: "mono" },
      { key: "department", label: "Department" },
      { key: "account", label: "Expense account", kind: "mono" },
      { key: "budget", label: "Budget", kind: "money", right: true },
      { key: "actual", label: "Actual", kind: "money", right: true },
      { key: "variance", label: "Remaining", kind: "money", right: true, get: (r) => num(r.budget) - num(r.actual) },
      { key: "used", label: "Used", kind: "pct", right: true, get: (r) => (num(r.budget) ? Math.round((num(r.actual) / num(r.budget)) * 100) : 0) },
      { key: "status", label: "Status", kind: "status", get: (r) => { const u = num(r.budget) ? (num(r.actual) / num(r.budget)) * 100 : 0; return u > 100 ? "Over budget" : u >= 80 ? "At risk" : "On track"; } },
    ],
    fields: [
      { key: "department", label: "Department", kind: "select", options: opts("Rooms", "Food & beverage", "Engineering", "Administration", "Sales & marketing"), required: true },
      { key: "account", label: "Expense account", kind: "select", options: acctOpts("Expense"), required: true },
      { key: "budget", label: "Budget", kind: "number", required: true },
      { key: "actual", label: "Actual", kind: "number" },
    ],
    acts: [],
  },

  /* ---------------- Expense management ---------------- */
  {
    id: "expenses",
    group: "Expense management",
    title: "Expense reports",
    subtitle: "Approve staff expenses and reimburse them.",
    noun: "expense report",
    icon: <Wallet20Regular />,
    page: "grid",
    prefix: "EX-",
    editable: ["Submitted"],
    initial: { status: "Submitted" },
    cols: [
      { key: "id", label: "Report", kind: "mono" },
      { key: "employee", label: "Employee" },
      { key: "purpose", label: "Purpose" },
      { key: "category", label: "Category" },
      { key: "date", label: "Date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "employee", label: "Employee", kind: "text", required: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "purpose", label: "Purpose", kind: "text", required: true, full: true },
      { key: "category", label: "Category", kind: "select", options: opts("Travel", "Meals", "Supplies", "Accommodation", "Other"), required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      setStatus("Approve", <Checkmark20Regular />, "Submitted", "Approved", (r) => `${r.id} approved`),
      setStatus("Reject", <Dismiss20Regular />, "Submitted", "Rejected", (r) => `${r.id} rejected`),
      {
        label: "Reimburse",
        icon: <Money20Regular />,
        show: (r) => r.status === "Approved",
        run: (r, d) => {
          const a = num(r.amount);
          const bank = rows(d, "banks")[0];
          if (!bank || num(bank.balance) < a) return { msg: "", err: `Insufficient balance in ${String(bank?.name ?? "the operating account")}.` };
          return { patch: { status: "Reimbursed" }, bank: -a, fx: [{ gl: "5200", d: a }], msg: `${String(r.employee)} reimbursed ${money(a)}` };
        },
      },
    ],
  },

  /* ---------------- Procurement ---------------- */
  {
    id: "pos",
    group: "Procurement and sourcing",
    title: "Purchase orders",
    subtitle: "Confirm orders with vendors and receive goods into stock.",
    noun: "purchase order",
    icon: <Cart20Regular />,
    page: "grid",
    prefix: "PO-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Purchase order", kind: "mono" },
      { key: "vendor", label: "Vendor" },
      { key: "description", label: "Description" },
      { key: "date", label: "Order date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "vendor", label: "Vendor", kind: "select", options: (d) => rows(d, "vendors").filter((v) => v.status === "Active").map((v) => ({ v: String(v.name), l: String(v.name) })), required: true, full: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
      { key: "date", label: "Order date", kind: "date", required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      setStatus("Confirm", <Checkmark20Regular />, "Draft", "Confirmed", (r) => `${r.id} confirmed with ${String(r.vendor)}`),
      {
        label: "Receive goods",
        icon: <Box20Regular />,
        show: (r) => r.status === "Confirmed",
        run: (r) => {
          const a = num(r.amount);
          return { patch: { status: "Received" }, fx: [{ gl: "1200", d: a }, { gl: "2100", d: a }], msg: `${r.id} received into inventory` };
        },
      },
      {
        label: "Cancel order",
        icon: <Dismiss20Regular />,
        show: (r) => r.status === "Draft" || r.status === "Confirmed",
        run: (r) => ({ patch: { status: "Cancelled" }, msg: `${r.id} cancelled` }),
      },
    ],
  },

  /* ---------------- Sales ---------------- */
  {
    id: "sos",
    group: "Sales and marketing",
    title: "Sales orders",
    subtitle: "Confirm orders and ship them to relieve inventory.",
    noun: "sales order",
    icon: <Cart20Regular />,
    page: "grid",
    prefix: "SO-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Sales order", kind: "mono" },
      { key: "customer", label: "Customer" },
      { key: "description", label: "Description" },
      { key: "date", label: "Order date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "customer", label: "Customer", kind: "select", options: (d) => rows(d, "customers").filter((v) => v.status === "Active").map((v) => ({ v: String(v.name), l: String(v.name) })), required: true, full: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
      { key: "date", label: "Order date", kind: "date", required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      setStatus("Confirm", <Checkmark20Regular />, "Draft", "Confirmed", (r) => `${r.id} confirmed`),
      {
        label: "Ship",
        icon: <Box20Regular />,
        show: (r) => r.status === "Confirmed",
        run: (r) => {
          const cost = Math.round(num(r.amount) * 0.6);
          return { patch: { status: "Shipped" }, fx: [{ gl: "5010", d: cost }, { gl: "1200", d: -cost }], msg: `${r.id} shipped` };
        },
      },
      {
        label: "Cancel order",
        icon: <Dismiss20Regular />,
        show: (r) => r.status === "Draft" || r.status === "Confirmed",
        run: (r) => ({ patch: { status: "Cancelled" }, msg: `${r.id} cancelled` }),
      },
    ],
  },

  /* ---------------- Inventory ---------------- */
  {
    id: "inventory",
    group: "Inventory management",
    title: "On-hand inventory",
    subtitle: "Stock levels by warehouse with reorder alerts.",
    noun: "item",
    icon: <Box20Regular />,
    page: "grid",
    prefix: "IT-",
    cols: [
      { key: "id", label: "Item number", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "warehouse", label: "Warehouse" },
      { key: "qty", label: "On hand", kind: "num", right: true },
      { key: "cost", label: "Unit cost", kind: "money", right: true },
      { key: "value", label: "Value", kind: "money", right: true, get: (r) => num(r.qty) * num(r.cost) },
      { key: "reorder", label: "Reorder level", kind: "num", right: true },
      { key: "status", label: "Status", kind: "status", get: (r) => (num(r.qty) <= num(r.reorder) ? "Low stock" : "In stock") },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "warehouse", label: "Warehouse", kind: "select", options: opts("Main store", "Kitchen", "Housekeeping", "Bar"), required: true },
      { key: "qty", label: "Quantity on hand", kind: "number", required: true },
      { key: "cost", label: "Unit cost", kind: "number", required: true },
      { key: "reorder", label: "Reorder level", kind: "number", required: true },
    ],
    acts: [
      { label: "Receive 10", icon: <Add20Regular />, show: () => true, run: (r) => ({ patch: { qty: num(r.qty) + 10 }, msg: `10 added to ${String(r.name)}` }) },
      {
        label: "Issue 10",
        icon: <Box20Regular />,
        show: (r) => num(r.qty) > 0,
        run: (r) => ({ patch: { qty: Math.max(0, num(r.qty) - 10) }, msg: `${Math.min(10, num(r.qty))} issued from ${String(r.name)}` }),
      },
    ],
  },
  /* =================== additional modules =================== */

  /* ---- General ledger ---- */
  {
    id: "periods",
    group: "General ledger",
    title: "Ledger calendar",
    subtitle: "Close a period to stop postings into it.",
    noun: "fiscal period",
    icon: <Calendar20Regular />,
    page: "grid",
    prefix: "FP-",
    editable: ["Open"],
    initial: { status: "Open" },
    cols: [
      { key: "id", label: "Period", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "start", label: "Start", kind: "date" },
      { key: "end", label: "End", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "start", label: "Start date", kind: "date", required: true },
      { key: "end", label: "End date", kind: "date", required: true },
    ],
    validate: (v) => (String(v.end) < String(v.start) ? "End date must be after the start date." : null),
    acts: [
      setStatus("Close period", <LockClosed20Regular />, "Open", "Closed", (r) => `${String(r.name)} closed`),
      setStatus("Reopen", <Checkmark20Regular />, "Closed", "Open", (r) => `${String(r.name)} reopened`),
    ],
  },
  {
    id: "dimensions",
    group: "General ledger",
    title: "Financial dimensions",
    subtitle: "Department, cost centre and property values used to analyse postings.",
    noun: "dimension value",
    icon: <TagMultiple20Regular />,
    page: "grid",
    prefix: "DM-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "Value", kind: "mono" },
      { key: "dimension", label: "Dimension" },
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "dimension", label: "Dimension", kind: "select", options: opts("Department", "Cost centre", "Property"), required: true },
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true, full: true },
    ],
    acts: [
      setStatus("Deactivate", <Dismiss20Regular />, "Active", "Inactive", (r) => `${String(r.name)} deactivated`),
      setStatus("Activate", <Checkmark20Regular />, "Inactive", "Active", (r) => `${String(r.name)} activated`),
    ],
  },
  {
    id: "rates",
    group: "General ledger",
    title: "Exchange rates",
    subtitle: "Rates used to convert foreign currency to naira.",
    noun: "exchange rate",
    icon: <Globe20Regular />,
    page: "grid",
    prefix: "FX-",
    cols: [
      { key: "id", label: "Rate", kind: "mono" },
      { key: "from", label: "From" },
      { key: "to", label: "To" },
      { key: "rate", label: "Rate", kind: "num", right: true },
      { key: "date", label: "Valid from", kind: "date" },
    ],
    fields: [
      { key: "from", label: "From currency", kind: "select", options: opts("USD", "EUR", "GBP"), required: true },
      { key: "to", label: "To currency", kind: "select", options: opts("NGN"), required: true },
      { key: "rate", label: "Rate", kind: "number", required: true },
      { key: "date", label: "Valid from", kind: "date", required: true },
    ],
    validate: (v) => (num(v.rate) > 0 ? null : "Rate must be greater than zero."),
    acts: [],
  },

  /* ---- Tax ---- */
  {
    id: "taxes",
    group: "Tax",
    title: "Sales tax codes",
    subtitle: "VAT and withholding tax codes.",
    noun: "tax code",
    icon: <DocumentBulletList20Regular />,
    page: "grid",
    prefix: "TX-",
    initial: { status: "Active" },
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "rate", label: "Rate", kind: "pct", right: true },
      { key: "account", label: "Ledger account", kind: "mono" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", options: opts("Output VAT", "Input VAT", "Withholding"), required: true },
      { key: "rate", label: "Rate (%)", kind: "number", required: true },
      { key: "account", label: "Ledger account", kind: "select", options: acctOpts("Liability"), required: true },
    ],
    acts: [
      setStatus("Deactivate", <Dismiss20Regular />, "Active", "Inactive", (r) => `${String(r.code)} deactivated`),
      setStatus("Activate", <Checkmark20Regular />, "Inactive", "Active", (r) => `${String(r.code)} activated`),
    ],
  },

  /* ---- Accounts payable / receivable credit notes ---- */
  {
    id: "ap_credits",
    group: "Accounts payable",
    title: "Vendor credit notes",
    subtitle: "Reduce what you owe a vendor.",
    noun: "vendor credit note",
    icon: <ArrowUndo20Regular />,
    page: "grid",
    prefix: "VC-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Credit note", kind: "mono" },
      { key: "vendor", label: "Vendor" },
      { key: "date", label: "Date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "vendor", label: "Vendor", kind: "select", options: vendorOpts, required: true, full: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "expense", label: "Expense account", kind: "select", options: acctOpts("Expense"), required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      {
        label: "Post",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r, d) => {
          const pe = periodErr(d, r.date);
          if (pe) return { msg: "", err: pe };
          const a = num(r.amount);
          return { patch: { status: "Posted" }, fx: [{ gl: "2010", d: -a }, { gl: String(r.expense || "5200"), d: -a }], msg: `${r.id} posted` };
        },
      },
    ],
  },
  {
    id: "ar_credits",
    group: "Accounts receivable",
    title: "Customer credit notes",
    subtitle: "Reduce what a customer owes you.",
    noun: "customer credit note",
    icon: <ArrowUndo20Regular />,
    page: "grid",
    prefix: "CN-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Credit note", kind: "mono" },
      { key: "customer", label: "Customer" },
      { key: "date", label: "Date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "customer", label: "Customer", kind: "select", options: customerOpts, required: true, full: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "revenue", label: "Revenue account", kind: "select", options: acctOpts("Revenue"), required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      {
        label: "Post",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r, d) => {
          const pe = periodErr(d, r.date);
          if (pe) return { msg: "", err: pe };
          const a = num(r.amount);
          return { patch: { status: "Posted" }, fx: [{ gl: String(r.revenue || "4010"), d: -a }, { gl: "1100", d: -a }], msg: `${r.id} posted` };
        },
      },
    ],
  },

  /* ---- Cash and bank ---- */
  {
    id: "transfers",
    group: "Cash and bank management",
    title: "Bank transfers",
    subtitle: "Move money between your own bank accounts.",
    noun: "bank transfer",
    icon: <ArrowSwap20Regular />,
    page: "grid",
    prefix: "TR-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Transfer", kind: "mono" },
      { key: "date", label: "Date", kind: "date" },
      { key: "fromName", label: "From", get: (r, d) => bankName(d, r.from) },
      { key: "toName", label: "To", get: (r, d) => bankName(d, r.to) },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "date", label: "Date", kind: "date", required: true, full: true },
      { key: "from", label: "From account", kind: "select", options: bankOpts, required: true },
      { key: "to", label: "To account", kind: "select", options: bankOpts, required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: (v) => (v.from === v.to ? "Choose two different bank accounts." : amountOk(v)),
    acts: [
      {
        label: "Post",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r, d) => {
          const a = num(r.amount);
          const src = rows(d, "banks").find((b) => b.id === r.from);
          if (!src || num(src.balance) < a) return { msg: "", err: `Insufficient balance in ${String(src?.name ?? "the source account")}.` };
          return { patch: { status: "Posted" }, bankMoves: [{ id: String(r.from), d: -a }, { id: String(r.to), d: a }], msg: `${money(a)} transferred` };
        },
      },
    ],
  },
  {
    id: "recon",
    group: "Cash and bank management",
    title: "Bank reconciliation",
    subtitle: "Match bank statement lines to your books.",
    noun: "statement line",
    icon: <Checkmark20Regular />,
    page: "grid",
    prefix: "BS-",
    initial: { status: "Unmatched" },
    cols: [
      { key: "id", label: "Line", kind: "mono" },
      { key: "bank", label: "Bank account" },
      { key: "date", label: "Date", kind: "date" },
      { key: "description", label: "Description" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "bank", label: "Bank account", kind: "select", options: (d) => rows(d, "banks").map((b) => ({ v: String(b.name), l: String(b.name) })), required: true, full: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "amount", label: "Amount (negative for payments)", kind: "number", required: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
    ],
    validate: (v) => (num(v.amount) === 0 ? "Amount can't be zero." : null),
    acts: [
      setStatus("Match", <Checkmark20Regular />, "Unmatched", "Matched", (r) => `${r.id} matched`),
      setStatus("Unmatch", <Dismiss20Regular />, "Matched", "Unmatched", (r) => `${r.id} unmatched`),
    ],
  },

  /* ---- Asset management ---- */
  {
    id: "maint",
    group: "Asset management",
    title: "Maintenance requests",
    subtitle: "Repairs and servicing for hotel assets.",
    noun: "maintenance request",
    icon: <Wrench20Regular />,
    page: "grid",
    prefix: "WO-",
    editable: ["Open"],
    initial: { status: "Open" },
    cols: [
      { key: "id", label: "Request", kind: "mono" },
      { key: "asset", label: "Asset" },
      { key: "issue", label: "Issue" },
      { key: "priority", label: "Priority" },
      { key: "date", label: "Reported", kind: "date" },
      { key: "amount", label: "Estimated cost", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "asset", label: "Asset", kind: "select", options: (d) => rows(d, "assets").map((a) => ({ v: String(a.name), l: String(a.name) })), required: true, full: true },
      { key: "issue", label: "Issue", kind: "text", required: true, full: true },
      { key: "priority", label: "Priority", kind: "select", options: opts("Low", "Normal", "High"), required: true },
      { key: "date", label: "Reported", kind: "date", required: true },
      { key: "amount", label: "Estimated cost", kind: "number", required: true },
    ],
    acts: [
      setStatus("Start work", <Wrench20Regular />, "Open", "In progress", (r) => `${r.id} started`),
      {
        label: "Complete",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "In progress",
        run: (r) => {
          const a = num(r.amount);
          return { patch: { status: "Completed" }, fx: [{ gl: "5200", d: a }, { gl: "2100", d: a }], msg: `${r.id} completed, ${money(a)} accrued` };
        },
      },
    ],
  },

  /* ---- Procurement ---- */
  {
    id: "reqs",
    group: "Procurement and sourcing",
    title: "Purchase requisitions",
    subtitle: "Internal requests that become purchase orders once approved.",
    noun: "requisition",
    icon: <ClipboardTaskListLtr20Regular />,
    page: "grid",
    prefix: "PR-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Requisition", kind: "mono" },
      { key: "requester", label: "Requester" },
      { key: "vendor", label: "Preferred vendor" },
      { key: "description", label: "Description" },
      { key: "date", label: "Date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "requester", label: "Requester", kind: "text", required: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "vendor", label: "Preferred vendor", kind: "select", options: vendorOpts, required: true, full: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      setStatus("Submit", <Checkmark20Regular />, "Draft", "Submitted", (r) => `${r.id} submitted for approval`),
      setStatus("Approve", <Checkmark20Regular />, "Submitted", "Approved", (r) => `${r.id} approved`),
      setStatus("Reject", <Dismiss20Regular />, "Submitted", "Rejected", (r) => `${r.id} rejected`),
      {
        label: "Convert to PO",
        icon: <Cart20Regular />,
        show: (r) => r.status === "Approved",
        run: (r) => ({
          patch: { status: "Converted" },
          create: { ent: "pos", values: { vendor: r.vendor ?? "", description: r.description ?? "", date: off(0), amount: num(r.amount), status: "Draft" } },
          msg: `${r.id} converted to a purchase order`,
        }),
      },
    ],
  },
  {
    id: "rfqs",
    group: "Procurement and sourcing",
    title: "Requests for quotation",
    subtitle: "Ask vendors to quote before you order.",
    noun: "request for quotation",
    icon: <Document20Regular />,
    page: "grid",
    prefix: "RQ-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "RFQ", kind: "mono" },
      { key: "vendor", label: "Vendor" },
      { key: "description", label: "Description" },
      { key: "date", label: "Date", kind: "date" },
      { key: "amount", label: "Quoted amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "vendor", label: "Vendor", kind: "select", options: vendorOpts, required: true, full: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "amount", label: "Quoted amount", kind: "number" },
    ],
    acts: [
      setStatus("Send", <Checkmark20Regular />, "Draft", "Sent", (r) => `${r.id} sent to ${String(r.vendor)}`),
      setStatus("Quote received", <Checkmark20Regular />, "Sent", "Received", (r) => `Quote received for ${r.id}`),
      setStatus("Close", <Dismiss20Regular />, "Received", "Closed", (r) => `${r.id} closed`),
    ],
  },

  /* ---- Sales ---- */
  {
    id: "quotes",
    group: "Sales and marketing",
    title: "Sales quotations",
    subtitle: "Send quotes and convert the ones you win into sales orders.",
    noun: "quotation",
    icon: <Document20Regular />,
    page: "grid",
    prefix: "SQ-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Quotation", kind: "mono" },
      { key: "customer", label: "Customer" },
      { key: "description", label: "Description" },
      { key: "date", label: "Date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "customer", label: "Customer", kind: "select", options: customerOpts, required: true, full: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      setStatus("Send", <Checkmark20Regular />, "Draft", "Sent", (r) => `${r.id} sent`),
      setStatus("Mark won", <Checkmark20Regular />, "Sent", "Won", (r) => `${r.id} won`),
      setStatus("Mark lost", <Dismiss20Regular />, "Sent", "Lost", (r) => `${r.id} lost`),
      {
        label: "Convert to order",
        icon: <Cart20Regular />,
        show: (r) => r.status === "Won",
        run: (r) => ({
          patch: { status: "Converted" },
          create: { ent: "sos", values: { customer: r.customer ?? "", description: r.description ?? "", date: off(0), amount: num(r.amount), status: "Draft" } },
          msg: `${r.id} converted to a sales order`,
        }),
      },
    ],
  },
  {
    id: "returns",
    group: "Sales and marketing",
    title: "Return orders",
    subtitle: "Customer returns and refunds.",
    noun: "return order",
    icon: <ArrowUndo20Regular />,
    page: "grid",
    prefix: "RO-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Return", kind: "mono" },
      { key: "customer", label: "Customer" },
      { key: "reason", label: "Reason" },
      { key: "date", label: "Date", kind: "date" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "customer", label: "Customer", kind: "select", options: customerOpts, required: true, full: true },
      { key: "reason", label: "Reason", kind: "text", required: true, full: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      setStatus("Approve", <Checkmark20Regular />, "Draft", "Approved", (r) => `${r.id} approved`),
      {
        label: "Issue credit",
        icon: <Money20Regular />,
        show: (r) => r.status === "Approved",
        run: (r) => {
          const a = num(r.amount);
          return { patch: { status: "Credited" }, fx: [{ gl: "4010", d: -a }, { gl: "1100", d: -a }], msg: `${money(a)} credited for ${r.id}` };
        },
      },
    ],
  },
  {
    id: "prices",
    group: "Sales and marketing",
    title: "Price lists",
    subtitle: "Selling prices for rooms, halls and menu items.",
    noun: "price",
    icon: <Tag20Regular />,
    page: "grid",
    prefix: "PL-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "Price", kind: "mono" },
      { key: "item", label: "Item" },
      { key: "price", label: "Unit price", kind: "money", right: true },
      { key: "from", label: "Valid from", kind: "date" },
      { key: "to", label: "Valid to", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "item", label: "Item", kind: "text", required: true, full: true },
      { key: "price", label: "Unit price", kind: "number", required: true },
      { key: "from", label: "Valid from", kind: "date", required: true },
      { key: "to", label: "Valid to", kind: "date", required: true },
    ],
    validate: (v) => (num(v.price) <= 0 ? "Price must be greater than zero." : String(v.to) < String(v.from) ? "Valid to must be after valid from." : null),
    acts: [
      setStatus("Deactivate", <Dismiss20Regular />, "Active", "Inactive", (r) => `${String(r.item)} price deactivated`),
      setStatus("Activate", <Checkmark20Regular />, "Inactive", "Active", (r) => `${String(r.item)} price activated`),
    ],
  },

  /* ---- Inventory ---- */
  {
    id: "warehouses",
    group: "Inventory management",
    title: "Warehouses",
    subtitle: "Stores, kitchens and bars that hold stock.",
    noun: "warehouse",
    icon: <BuildingMultiple20Regular />,
    page: "grid",
    prefix: "WH-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "Warehouse", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "location", label: "Location" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "type", label: "Type", kind: "select", options: opts("Store", "Kitchen", "Bar", "Housekeeping"), required: true },
      { key: "location", label: "Location", kind: "text" },
    ],
    acts: [
      setStatus("Deactivate", <Dismiss20Regular />, "Active", "Inactive", (r) => `${String(r.name)} deactivated`),
      setStatus("Activate", <Checkmark20Regular />, "Inactive", "Active", (r) => `${String(r.name)} activated`),
    ],
  },
  {
    id: "stock_transfers",
    group: "Inventory management",
    title: "Stock transfers",
    subtitle: "Move an item's stock to another warehouse.",
    noun: "stock transfer",
    icon: <ArrowSwap20Regular />,
    page: "grid",
    prefix: "ST-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Transfer", kind: "mono" },
      { key: "itemName", label: "Item", get: (r, d) => String(rows(d, "inventory").find((i) => i.id === r.item)?.name ?? r.item ?? "") },
      { key: "toWh", label: "To warehouse" },
      { key: "date", label: "Date", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "item", label: "Item", kind: "select", options: itemOpts, required: true, full: true },
      { key: "toWh", label: "To warehouse", kind: "select", options: (d) => rows(d, "warehouses").filter((w) => w.status === "Active").map((w) => ({ v: String(w.name), l: String(w.name) })), required: true },
      { key: "date", label: "Date", kind: "date", required: true },
    ],
    acts: [
      setStatus("Ship", <Box20Regular />, "Draft", "In transit", (r) => `${r.id} shipped`),
      {
        label: "Receive",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "In transit",
        run: (r) => ({ patch: { status: "Received" }, other: [{ ent: "inventory", id: String(r.item), patch: { warehouse: r.toWh ?? "" } }], msg: `${r.id} received at ${String(r.toWh)}` }),
      },
    ],
  },
  {
    id: "counts",
    group: "Inventory management",
    title: "Stock counts",
    subtitle: "Count stock and post the difference.",
    noun: "stock count",
    icon: <ClipboardTaskListLtr20Regular />,
    page: "grid",
    prefix: "SC-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Count", kind: "mono" },
      { key: "itemName", label: "Item", get: (r, d) => String(rows(d, "inventory").find((i) => i.id === r.item)?.name ?? r.item ?? "") },
      { key: "date", label: "Date", kind: "date" },
      { key: "book", label: "Book qty", kind: "num", right: true, get: (r, d) => (r.status === "Posted" ? num(r.book) : num(rows(d, "inventory").find((i) => i.id === r.item)?.qty)) },
      { key: "counted", label: "Counted", kind: "num", right: true },
      { key: "variance", label: "Variance", kind: "num", right: true, get: (r, d) => num(r.counted) - (r.status === "Posted" ? num(r.book) : num(rows(d, "inventory").find((i) => i.id === r.item)?.qty)) },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "item", label: "Item", kind: "select", options: itemOpts, required: true, full: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "counted", label: "Counted quantity", kind: "number", required: true },
    ],
    acts: [
      {
        label: "Post count",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r, d) => {
          const item = rows(d, "inventory").find((i) => i.id === r.item);
          if (!item) return { msg: "", err: "The counted item no longer exists." };
          return { patch: { status: "Posted", book: num(item.qty) }, other: [{ ent: "inventory", id: item.id, patch: { qty: num(r.counted) } }], msg: `${r.id} posted, ${String(item.name)} set to ${num(r.counted)}` };
        },
      },
    ],
  },

  /* ---- Warehouse management ---- */
  {
    id: "picks",
    group: "Warehouse management",
    title: "Pick lists",
    subtitle: "Pick and pack goods for sales orders.",
    noun: "pick list",
    icon: <Box20Regular />,
    page: "grid",
    prefix: "PK-",
    editable: ["Open"],
    initial: { status: "Open" },
    cols: [
      { key: "id", label: "Pick list", kind: "mono" },
      { key: "order", label: "Sales order", kind: "mono" },
      { key: "warehouse", label: "Warehouse" },
      { key: "date", label: "Date", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "order", label: "Sales order", kind: "select", options: (d) => rows(d, "sos").map((s) => ({ v: s.id, l: `${s.id} · ${String(s.customer)}` })), required: true, full: true },
      { key: "warehouse", label: "Warehouse", kind: "select", options: (d) => rows(d, "warehouses").map((w) => ({ v: String(w.name), l: String(w.name) })), required: true },
      { key: "date", label: "Date", kind: "date", required: true },
    ],
    acts: [
      setStatus("Pick", <Checkmark20Regular />, "Open", "Picked", (r) => `${r.id} picked`),
      setStatus("Pack", <Box20Regular />, "Picked", "Packed", (r) => `${r.id} packed`),
    ],
  },

  /* ---- Production control ---- */
  {
    id: "boms",
    group: "Production control",
    title: "Bills of materials",
    subtitle: "Recipes and the cost of one batch.",
    noun: "bill of materials",
    icon: <Beaker20Regular />,
    page: "grid",
    prefix: "BM-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "BOM", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "yieldQty", label: "Yield per batch", kind: "num", right: true },
      { key: "cost", label: "Cost per batch", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "yieldQty", label: "Yield per batch", kind: "number", required: true },
      { key: "cost", label: "Cost per batch", kind: "number", required: true },
    ],
    acts: [
      setStatus("Deactivate", <Dismiss20Regular />, "Active", "Inactive", (r) => `${String(r.name)} deactivated`),
      setStatus("Activate", <Checkmark20Regular />, "Inactive", "Active", (r) => `${String(r.name)} activated`),
    ],
  },
  {
    id: "prod_orders",
    group: "Production control",
    title: "Production orders",
    subtitle: "Plan, start and finish production batches.",
    noun: "production order",
    icon: <Beaker20Regular />,
    page: "grid",
    prefix: "MO-",
    editable: ["Planned"],
    initial: { status: "Planned" },
    cols: [
      { key: "id", label: "Order", kind: "mono" },
      { key: "bom", label: "Recipe" },
      { key: "batches", label: "Batches", kind: "num", right: true },
      { key: "total", label: "Planned cost", kind: "money", right: true, get: (r, d) => num(r.batches) * num(rows(d, "boms").find((b) => b.name === r.bom)?.cost) },
      { key: "date", label: "Date", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "bom", label: "Recipe", kind: "select", options: (d) => rows(d, "boms").filter((b) => b.status === "Active").map((b) => ({ v: String(b.name), l: String(b.name) })), required: true, full: true },
      { key: "batches", label: "Batches", kind: "number", required: true },
      { key: "date", label: "Date", kind: "date", required: true },
    ],
    validate: (v) => (num(v.batches) > 0 ? null : "Batches must be greater than zero."),
    acts: [
      setStatus("Start", <Checkmark20Regular />, "Planned", "Started", (r) => `${r.id} started`),
      setStatus("Finish", <Checkmark20Regular />, "Started", "Finished", (r) => `${r.id} finished`),
    ],
  },

  /* ---- Project management ---- */
  {
    id: "projects",
    group: "Project management",
    title: "Projects",
    subtitle: "Events, refurbishments and other jobs with a budget.",
    noun: "project",
    icon: <Briefcase20Regular />,
    page: "grid",
    prefix: "PJ-",
    initial: { status: "In progress" },
    cols: [
      { key: "id", label: "Project", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "customer", label: "Customer" },
      { key: "budget", label: "Budget", kind: "money", right: true },
      { key: "cost", label: "Cost to date", kind: "money", right: true, get: (r, d) => rows(d, "timesheets").filter((t) => t.project === r.id && t.status === "Approved").reduce((s, t) => s + num(t.hours) * num(t.rate), 0) },
      { key: "start", label: "Start", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "customer", label: "Customer", kind: "select", options: customerOpts, required: true },
      { key: "budget", label: "Budget", kind: "number", required: true },
      { key: "start", label: "Start date", kind: "date", required: true },
    ],
    acts: [setStatus("Complete", <Checkmark20Regular />, "In progress", "Completed", (r) => `${String(r.name)} completed`)],
  },
  {
    id: "timesheets",
    group: "Project management",
    title: "Timesheets",
    subtitle: "Approve hours booked to projects.",
    noun: "timesheet",
    icon: <Clock20Regular />,
    page: "grid",
    prefix: "TS-",
    editable: ["Submitted"],
    initial: { status: "Submitted" },
    cols: [
      { key: "id", label: "Timesheet", kind: "mono" },
      { key: "employee", label: "Employee" },
      { key: "project", label: "Project", kind: "mono" },
      { key: "date", label: "Date", kind: "date" },
      { key: "hours", label: "Hours", kind: "num", right: true },
      { key: "cost", label: "Cost", kind: "money", right: true, get: (r) => num(r.hours) * num(r.rate) },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "employee", label: "Employee", kind: "select", options: (d) => rows(d, "workers").filter((w) => w.status === "Active").map((w) => ({ v: String(w.name), l: String(w.name) })), required: true },
      { key: "project", label: "Project", kind: "select", options: (d) => rows(d, "projects").map((p) => ({ v: p.id, l: `${p.id} · ${String(p.name)}` })), required: true },
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "hours", label: "Hours", kind: "number", required: true },
      { key: "rate", label: "Hourly rate", kind: "number", required: true },
    ],
    validate: (v) => (num(v.hours) <= 0 ? "Hours must be greater than zero." : null),
    acts: [
      setStatus("Approve", <Checkmark20Regular />, "Submitted", "Approved", (r) => `${r.id} approved`),
      setStatus("Reject", <Dismiss20Regular />, "Submitted", "Rejected", (r) => `${r.id} rejected`),
    ],
  },

  /* ---- Human resources ---- */
  {
    id: "workers",
    group: "Human resources",
    title: "Workers",
    subtitle: "Employees and their positions.",
    noun: "worker",
    icon: <People20Regular />,
    page: "grid",
    prefix: "W-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "Worker", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "department", label: "Department" },
      { key: "position", label: "Position" },
      { key: "hired", label: "Hire date", kind: "date" },
      { key: "salary", label: "Monthly salary", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "department", label: "Department", kind: "select", options: opts("Rooms", "Food & beverage", "Engineering", "Administration", "Sales & marketing"), required: true },
      { key: "position", label: "Position", kind: "text", required: true },
      { key: "hired", label: "Hire date", kind: "date", required: true },
      { key: "salary", label: "Monthly salary", kind: "number", required: true },
    ],
    acts: [
      setStatus("Terminate", <Dismiss20Regular />, "Active", "Terminated", (r) => `${String(r.name)} terminated`),
      setStatus("Reinstate", <Checkmark20Regular />, "Terminated", "Active", (r) => `${String(r.name)} reinstated`),
    ],
  },
  {
    id: "leave",
    group: "Human resources",
    title: "Leave requests",
    subtitle: "Approve time off.",
    noun: "leave request",
    icon: <Calendar20Regular />,
    page: "grid",
    prefix: "LV-",
    editable: ["Submitted"],
    initial: { status: "Submitted" },
    cols: [
      { key: "id", label: "Request", kind: "mono" },
      { key: "employee", label: "Employee" },
      { key: "type", label: "Leave type" },
      { key: "from", label: "From", kind: "date" },
      { key: "to", label: "To", kind: "date" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "employee", label: "Employee", kind: "select", options: (d) => rows(d, "workers").filter((w) => w.status === "Active").map((w) => ({ v: String(w.name), l: String(w.name) })), required: true, full: true },
      { key: "type", label: "Leave type", kind: "select", options: opts("Annual", "Sick", "Maternity", "Paternity", "Unpaid"), required: true },
      { key: "from", label: "From", kind: "date", required: true },
      { key: "to", label: "To", kind: "date", required: true },
    ],
    validate: (v) => (String(v.to) < String(v.from) ? "The end date must be after the start date." : null),
    acts: [
      setStatus("Approve", <Checkmark20Regular />, "Submitted", "Approved", (r) => `${r.id} approved`),
      setStatus("Reject", <Dismiss20Regular />, "Submitted", "Rejected", (r) => `${r.id} rejected`),
    ],
  },
  {
    id: "payroll",
    group: "Human resources",
    title: "Payroll runs",
    subtitle: "Post payroll to the ledger, then pay it.",
    noun: "payroll run",
    icon: <Money20Regular />,
    page: "grid",
    prefix: "PY-",
    editable: ["Draft"],
    initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Run", kind: "mono" },
      { key: "period", label: "Period" },
      { key: "headcount", label: "Workers", kind: "num", right: true, get: (_r, d) => rows(d, "workers").filter((w) => w.status === "Active").length },
      { key: "amount", label: "Gross pay", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "period", label: "Period", kind: "text", required: true },
      { key: "amount", label: "Gross pay", kind: "number", required: true },
    ],
    validate: amountOk,
    acts: [
      {
        label: "Post",
        icon: <Checkmark20Regular />,
        show: (r) => r.status === "Draft",
        run: (r) => {
          const a = num(r.amount);
          return { patch: { status: "Posted" }, fx: [{ gl: "5100", d: a }, { gl: "2100", d: a }], msg: `${r.id} posted to the ledger` };
        },
      },
      {
        label: "Pay",
        icon: <Money20Regular />,
        show: (r) => r.status === "Posted",
        run: (r, d) => {
          const a = num(r.amount);
          const bank = rows(d, "banks")[0];
          if (!bank || num(bank.balance) < a) return { msg: "", err: `Insufficient balance in ${String(bank?.name ?? "the operating account")}.` };
          return { patch: { status: "Paid" }, bank: -a, fx: [{ gl: "2100", d: -a }], msg: `${r.id} paid from ${String(bank.name)}` };
        },
      },
    ],
  },

  /* ---- Organization administration ---- */
  {
    id: "legal",
    group: "Organization administration",
    title: "Legal entities",
    subtitle: "Companies that keep their own books.",
    noun: "legal entity",
    icon: <Building20Regular />,
    page: "grid",
    prefix: "LE-",
    initial: { status: "Active" },
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "country", label: "Country" },
      { key: "currency", label: "Currency" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true },
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "country", label: "Country", kind: "text", required: true },
      { key: "currency", label: "Currency", kind: "select", options: opts("NGN", "USD", "GBP", "EUR"), required: true },
    ],
    acts: [
      setStatus("Deactivate", <Dismiss20Regular />, "Active", "Inactive", (r) => `${String(r.name)} deactivated`),
      setStatus("Activate", <Checkmark20Regular />, "Inactive", "Active", (r) => `${String(r.name)} activated`),
    ],
  },
  {
    id: "units",
    group: "Organization administration",
    title: "Operating units",
    subtitle: "Departments and cost centres.",
    noun: "operating unit",
    icon: <Organization20Regular />,
    page: "grid",
    prefix: "OU-",
    cols: [
      { key: "id", label: "Unit", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "manager", label: "Manager" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "type", label: "Type", kind: "select", options: opts("Department", "Cost centre"), required: true },
      { key: "manager", label: "Manager", kind: "text" },
    ],
    acts: [],
  },
  {
    id: "numseq",
    group: "Organization administration",
    title: "Number sequences",
    subtitle: "How record numbers are generated.",
    noun: "number sequence",
    icon: <DocumentBulletList20Regular />,
    page: "grid",
    prefix: "NS-",
    cols: [
      { key: "id", label: "Sequence", kind: "mono" },
      { key: "name", label: "Used for" },
      { key: "pfx", label: "Prefix", kind: "mono" },
      { key: "next", label: "Next number", kind: "num", right: true, get: (r, d) => { const e = ENTITIES.find((x) => x.prefix === r.pfx); return e ? Number(nextId(e, rows(d, e.id)).slice((e.prefix ?? "").length)) : num(r.next); } },
    ],
    fields: [
      { key: "name", label: "Used for", kind: "text", required: true, full: true },
      { key: "pfx", label: "Prefix", kind: "text", required: true },
      { key: "next", label: "Next number", kind: "number", required: true },
    ],
    acts: [],
  },

  /* ---- System administration ---- */
  {
    id: "users",
    group: "System administration",
    title: "Users",
    subtitle: "People who can sign in to Finance and Operations.",
    noun: "user",
    icon: <Person20Regular />,
    page: "grid",
    prefix: "U-",
    initial: { status: "Active" },
    cols: [
      { key: "id", label: "User", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "role", label: "Security role" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "email", label: "Email", kind: "text", required: true },
      { key: "role", label: "Security role", kind: "select", options: (d) => rows(d, "roles").map((r) => ({ v: String(r.name), l: String(r.name) })), required: true, full: true },
    ],
    acts: [
      setStatus("Disable", <Dismiss20Regular />, "Active", "Disabled", (r) => `${String(r.name)} disabled`),
      setStatus("Enable", <Checkmark20Regular />, "Disabled", "Active", (r) => `${String(r.name)} enabled`),
    ],
  },
  {
    id: "roles",
    group: "System administration",
    title: "Security roles",
    subtitle: "What each role is allowed to do.",
    noun: "security role",
    icon: <Shield20Regular />,
    page: "grid",
    prefix: "SR-",
    cols: [
      { key: "id", label: "Role", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
      { key: "modules", label: "Modules" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "description", label: "Description", kind: "text", required: true, full: true },
      { key: "modules", label: "Modules", kind: "text", required: true, full: true },
    ],
    acts: [],
  },
  {
    id: "workflows",
    group: "System administration",
    title: "Approval workflows",
    subtitle: "Rules that route records for approval.",
    noun: "workflow",
    icon: <Flow20Regular />,
    page: "grid",
    prefix: "WF-",
    initial: { status: "Enabled" },
    cols: [
      { key: "id", label: "Workflow", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "applies", label: "Applies to" },
      { key: "threshold", label: "Above amount", kind: "money", right: true },
      { key: "approver", label: "Approver role" },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "applies", label: "Applies to", kind: "select", options: opts("Vendor invoices", "Purchase orders", "Expense reports", "Journal entries", "Payroll runs"), required: true },
      { key: "threshold", label: "Above amount", kind: "number", required: true },
      { key: "approver", label: "Approver role", kind: "select", options: (d) => rows(d, "roles").map((r) => ({ v: String(r.name), l: String(r.name) })), required: true, full: true },
    ],
    acts: [
      setStatus("Disable", <Dismiss20Regular />, "Enabled", "Disabled", (r) => `${String(r.name)} disabled`),
      setStatus("Enable", <Checkmark20Regular />, "Disabled", "Enabled", (r) => `${String(r.name)} enabled`),
    ],
  },
  {
    id: "audit",
    group: "System administration",
    title: "Audit log",
    subtitle: "Every create, change, post and delete, newest first.",
    noun: "entry",
    icon: <History20Regular />,
    page: "grid",
    readOnly: true,
    cols: [
      { key: "when", label: "When" },
      { key: "user", label: "User" },
      { key: "entity", label: "Page" },
      { key: "record", label: "Record", kind: "mono" },
      { key: "action", label: "Action" },
      { key: "detail", label: "Detail" },
    ],
    fields: [],
    acts: [],
  },

  /* ---- Reports ---- */
  { id: "pnl", group: "Reports", title: "Income statement", subtitle: "Revenue less expenses from the ledger.", noun: "", icon: <DataPie20Regular />, page: "pnl", cols: [], fields: [], acts: [] },
  { id: "bs", group: "Reports", title: "Balance sheet", subtitle: "Assets, liabilities and equity from the ledger.", noun: "", icon: <Table20Regular />, page: "bs", cols: [], fields: [], acts: [] },
];

/** backend modules (route segment, title) - each opens the generic resource page */
const MODULES: [string, string][] = [["ai", "AI"], ["assets", "Asset management"], ["clm", "Contracts (CLM)"], ["commerce", "Commerce"], ["controltower", "Control tower"], ["cpq", "Configure, price, quote"], ["extensibility", "Extensibility"], ["industry", "Industry solutions"], ["insights", "Insights"], ["intercompany", "Intercompany"], ["inventory", "Inventory"], ["manufacturing", "Manufacturing"], ["masterdata", "Master data"], ["pim", "Product information"], ["planning", "Planning"], ["plm", "Product lifecycle"], ["procurement", "Procurement"], ["projects", "Projects"], ["quality", "Quality"], ["sales", "Sales"], ["sustainability", "Sustainability"], ["trade", "Trade and logistics"], ["warehouse", "Warehouse"], ["workforce", "Workforce"]];
/** pages that must also refresh when the key page changes */
const DEPENDENTS: Record<string, string[]> = { pos: ["inventory"], sos: ["inventory", "picks"], inventory: ["stock_transfers", "counts"], stock_transfers: ["inventory"], counts: ["inventory"], prod_orders: ["inventory"], vendors: ["pos", "reqs", "rfqs"], customers: ["sos", "quotes", "returns"], workers: ["timesheets", "leave", "units"], projects: ["timesheets"], quotes: ["sos"], reqs: ["pos"] };
const GROUP_ORDER = ["Workspaces", "General ledger", "Accounts payable", "Accounts receivable", "Cash and bank management", "Fixed assets", "Asset management", "Budgeting", "Expense management", "Tax", "Procurement and sourcing", "Sales and marketing", "Inventory management", "Warehouse management", "Production control", "Project management", "Human resources", "Reports", "Organization administration", "System administration"];

/* =====================================================================
   Data: nothing is built in
   ===================================================================== */
/** No built-in rows: every number on screen comes from an API. */
const seed = (): Data => ({});

/* =====================================================================
   Applying the result of an action across the data set
   ===================================================================== */
const nextId = (ent: Entity | undefined, list: Row[]): string => {
  const p = ent?.prefix ?? "";
  const mx = list.reduce((m, r) => Math.max(m, Number(r.id.slice(p.length)) || 0), 0);
  return p + String(mx + 1).padStart(4, "0");
};

const audit = (d: Data, entTitle: string, rec: string, action: string, detail: string): Data => ({
  ...d,
  audit: [R(`AU-${String(rows(d, "audit").length + 1).padStart(4, "0")}`, { when: new Date().toLocaleString("en-GB"), user: "You", entity: entTitle, record: rec, action, detail }), ...rows(d, "audit")],
});

const apply = (d: Data, entId: string, id: string, res: Res): Data => {
  const next: Data = { ...d };
  next[entId] = rows(d, entId).map((r) => (r.id === id ? ({ ...r, ...(res.patch ?? {}) } as Row) : r));
  const bump = (acct: string, delta: number) => {
    next.coa = rows(next, "coa").map((r) => (r.id === acct ? { ...r, balance: num(r.balance) + delta } : r));
  };
  (res.fx ?? []).forEach((f) => bump(f.gl, f.d));
  const b = res.bank;
  if (b) {
    next.banks = rows(next, "banks").map((r, i) => (i === 0 ? { ...r, balance: num(r.balance) + b } : r));
    bump("1010", b);
  }
  (res.bankMoves ?? []).forEach((m) => {
    next.banks = rows(next, "banks").map((r) => (r.id === m.id ? { ...r, balance: num(r.balance) + m.d } : r));
  });
  (res.other ?? []).forEach((o) => {
    next[o.ent] = rows(next, o.ent).map((r) => (r.id === o.id ? ({ ...r, ...o.patch } as Row) : r));
  });
  if (res.create) {
    const target = ENTITIES.find((e) => e.id === res.create?.ent);
    const list = rows(next, res.create.ent);
    next[res.create.ent] = [...list, { id: nextId(target, list), ...res.create.values }];
  }
  return next;
};

/* =====================================================================
   Record form (New / Edit)
   ===================================================================== */
const RecordDialog: React.FC<{
  ent: Entity;
  data: Data;
  row: Row | null;
  onClose: () => void;
  onSave: (v: Record<string, Val>) => string | null | Promise<string | null>;
}> = ({ ent, data, row, onClose, onSave }) => {
  const [v, setV] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    ent.fields.forEach((f) => {
      const cur = row ? row[f.key] : undefined;
      o[f.key] = cur !== undefined ? String(cur) : f.kind === "date" ? off(0) : "";
    });
    return o;
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const missing = ent.fields.find((f) => f.required && !(v[f.key] ?? "").trim());
    if (missing) return setErr(`${missing.label} is required.`);
    const out: Record<string, Val> = {};
    ent.fields.forEach((f) => {
      const raw = (v[f.key] ?? "").trim();
      out[f.key] = f.kind === "number" ? Number(raw) || 0 : raw;
    });
    setBusy(true);
    setErr(null);
    try {
      const bad = await onSave(out);
      if (bad) setErr(bad);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {row ? `Edit ${ent.noun}` : `New ${ent.noun}`}
          </DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              {ent.fields.map((f) => {
                const options = typeof f.options === "function" ? f.options(data) : f.options ?? [];
                const locked = !!row && !!ent.idKey && f.key === ent.idKey;
                return (
                  <div key={f.key} className={f.full ? styles.formFull : undefined}>
                    <Field label={f.label} required={!!f.required}>
                      {f.kind === "select" ? (
                        <Select value={v[f.key] ?? ""} onChange={(_, d) => setV({ ...v, [f.key]: d.value })}>
                          <option value="">Select…</option>
                          {options.map((o) => (
                            <option key={o.v} value={o.v}>
                              {o.l}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          type={f.kind === "number" ? "number" : f.kind === "date" ? "date" : "text"}
                          value={v[f.key] ?? ""}
                          disabled={locked}
                          onChange={(_, d) => setV({ ...v, [f.key]: d.value })}
                        />
                      )}
                    </Field>
                  </div>
                );
              })}
            </div>
            {err && (
              <div className={styles.formErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button className={styles.primary} appearance="primary" disabled={busy} onClick={submit}>
              {busy ? "Working…" : row ? "Save" : "Create"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* =====================================================================
   Grid page (every list in the module)
   ===================================================================== */
/** connection to the F&O API: ok = the operations pages read and write the backend; otherwise they show local demo data */
interface Live {
  ok: boolean;
  ctx: Ctx | null;
  reload: (only?: string) => Promise<void>;
}
interface PageProps {
  live: Live;
  data: Data;
  setData: React.Dispatch<React.SetStateAction<Data>>;
  say: (msg: string, err?: boolean) => void;
  go: (id: string) => void;
}

const EntityPage: React.FC<PageProps & { ent: Entity }> = ({ ent, data, setData, say, live }) => {
  const bound = live.ok && live.ctx ? BINDINGS[ent.id] : undefined;
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("All");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<"new" | "edit" | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);

  const list = rows(data, ent.id);
  const statusCol = ent.cols.find((c) => c.kind === "status");
  const statuses = statusCol ? Array.from(new Set(list.map((r) => String(val(statusCol, r, data))))) : [];

  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = list.filter((r) => {
      if (statusCol && statusF !== "All" && String(val(statusCol, r, data)) !== statusF) return false;
      return !needle || ent.cols.some((c) => show(c, val(c, r, data)).toLowerCase().includes(needle));
    });
    if (sort) {
      const col = ent.cols.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.dir;
        out = [...out].sort((a, b) => {
          const x = val(col, a, data);
          const y = val(col, b, data);
          return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) * dir;
        });
      }
    }
    return out;
  }, [list, q, statusF, sort, ent, statusCol, data]);

  const sel = list.find((r) => r.id === selId) ?? null;
  const canChange = !ent.readOnly && !!sel && (!ent.editable || ent.editable.includes(String(sel.status)));
  const moneyCol = ent.cols.find((c) => c.kind === "money");
  const moneyTotal = moneyCol ? view.reduce((t, r) => t + num(val(moneyCol, r, data)), 0) : 0;

  const runAct = async (a: Act) => {
    if (!sel) return;
    if (bound && live.ctx) {
      const fn = bound.act?.[a.label];
      if (!fn) return say(`"${a.label}" is not available against the live API.`, true);
      try {
        const msg = await fn(sel, live.ctx);
        await live.reload(ent.id);
        say(msg);
      } catch (e) {
        say(errText(e), true);
      }
      return;
    }
    const res = a.run(sel, data);
    if (res.err) return say(res.err, true);
    setData((d) => audit(apply(d, ent.id, sel.id, res), ent.title, sel.id, a.label, res.msg));
    say(res.msg);
  };

  const save = async (values: Record<string, Val>): Promise<string | null> => {
    const bad = ent.validate ? ent.validate(values) : null;
    if (bad) return bad;
    if (bound && live.ctx) {
      try {
        if (form === "edit" && sel) {
          if (!bound.update) return `A ${ent.noun} can't be edited here - delete it and create it again.`;
          await bound.update(sel, values, live.ctx);
          say(`${sel.id} saved`);
        } else {
          if (!bound.create) return bound.noCreate ?? `New ${ent.noun} isn't available here.`;
          await bound.create(values, live.ctx);
          say(`${ent.noun[0]?.toUpperCase() ?? ""}${ent.noun.slice(1)} created`);
        }
        await live.reload(ent.id);
        setForm(null);
        return null;
      } catch (e) {
        return errText(e);
      }
    }
    if (form === "edit" && sel) {
      setData((d) => audit({ ...d, [ent.id]: rows(d, ent.id).map((r) => (r.id === sel.id ? ({ ...r, ...values } as Row) : r)) }, ent.title, sel.id, "Edited", "Record changed"));
      say(`${ent.noun[0]?.toUpperCase() ?? ""}${ent.noun.slice(1)} ${sel.id} saved`);
    } else {
      let id: string;
      if (ent.idKey) {
        id = String(values[ent.idKey] ?? "");
        if (list.some((r) => r.id === id)) return `A ${ent.noun} numbered ${id} already exists.`;
      } else {
        id = nextId(ent, list);
      }
      const row: Row = { id, ...values, ...(ent.initial ?? {}) };
      setData((d) => audit({ ...d, [ent.id]: [...rows(d, ent.id), row] }, ent.title, id, "Created", `${id} created`));
      setSelId(id);
      say(`${id} created`);
    }
    setForm(null);
    return null;
  };

  const remove = async () => {
    if (!sel) return;
    if (bound && live.ctx) {
      try {
        if (!bound.remove) throw new Error(`A ${ent.noun} can't be deleted here.`);
        await bound.remove(sel, live.ctx);
        await live.reload(ent.id);
        say(`${sel.id} deleted`);
        setSelId(null);
      } catch (e) {
        say(errText(e), true);
      }
      setConfirmDel(false);
      return;
    }
    setData((d) => audit({ ...d, [ent.id]: rows(d, ent.id).filter((r) => r.id !== sel.id) }, ent.title, sel.id, "Deleted", `${sel.id} deleted`));
    say(`${sel.id} deleted`);
    setSelId(null);
    setConfirmDel(false);
  };

  const exportCsv = () => {
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const lines = [ent.cols.map((c) => esc(c.label)).join(",")].concat(view.map((r) => ent.cols.map((c) => esc(show(c, val(c, r, data)))).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ent.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortBy = (key: string) => setSort((s) => (s && s.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }));

  return (
    <>
      <div className={styles.pane} role="toolbar" aria-label="Actions">
        {!ent.readOnly && bound && (
          <>
            <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setForm("new")}>
              New
            </Button>
            <Button appearance="subtle" icon={<Edit20Regular />} disabled={!canChange || ent.fields.length === 0} onClick={() => setForm("edit")}>
              Edit
            </Button>
            <Button appearance="subtle" icon={<Delete20Regular />} disabled={!canChange} onClick={() => setConfirmDel(true)}>
              Delete
            </Button>
          </>
        )}
        {bound && ent.acts.length > 0 && <span className={styles.sep} />}
        {bound && ent.acts.map((a) => (
          <Button key={a.label} appearance="subtle" icon={a.icon as React.ReactElement} disabled={!sel || !a.show(sel)} onClick={() => runAct(a)}>
            {a.label}
          </Button>
        ))}
        <span className={styles.grow} />
        <Button appearance="subtle" icon={<ArrowDownload20Regular />} onClick={exportCsv}>
          Export
        </Button>
      </div>

      <div className={styles.filters}>
        <SearchBox className={styles.search} placeholder={`Search ${ent.title.toLowerCase()}`} value={q} onChange={(_, d) => setQ(d.value)} />
        {statusCol && (
          <Select className={styles.statusSel} value={statusF} onChange={(_, d) => setStatusF(d.value)} aria-label="Filter by status">
            <option value="All">All statuses</option>
            {statuses.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        )}
      </div>

      <div className={styles.work}>
        <div className={styles.gridWrap}>
          <table className={styles.grid}>
            <thead>
              <tr>
                {ent.cols.map((c) => (
                  <th key={c.key} className={c.right ? styles.right : undefined} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                    <button type="button" className={styles.th} onClick={() => sortBy(c.key)}>
                      {c.label}
                      {sort?.key === c.key && (sort.dir === 1 ? <ChevronUp12Regular /> : <ChevronDown12Regular />)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {view.map((r) => (
                <tr
                  key={r.id}
                  className={r.id === selId ? styles.rowSel : undefined}
                  aria-selected={r.id === selId}
                  tabIndex={0}
                  onClick={() => setSelId(r.id)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setSelId(r.id))}
                >
                  {ent.cols.map((c) => {
                    const v = val(c, r, data);
                    return (
                      <td key={c.key} className={`${c.right ? styles.right : ""} ${c.kind === "mono" ? styles.mono : ""}`}>
                        {c.kind === "status" ? <Pill text={String(v)} /> : show(c, v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {view.length === 0 && (
                <tr>
                  <td colSpan={ent.cols.length} className={styles.empty}>
                    No {ent.title.toLowerCase()} match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {sel && (
          <aside className={styles.detail} aria-label="Details">
            <div className={styles.detailHead}>
              <strong>{sel.id}</strong>
              <Button appearance="subtle" size="small" icon={<Dismiss20Regular />} aria-label="Close details" onClick={() => setSelId(null)} />
            </div>
            <dl className={styles.dl}>
              {ent.cols
                .filter((c) => c.key !== "id")
                .map((c) => {
                  const v = val(c, sel, data);
                  return (
                    <div key={c.key} className={styles.dlRow}>
                      <dt>{c.label}</dt>
                      <dd>{c.kind === "status" ? <Pill text={String(v)} /> : show(c, v)}</dd>
                    </div>
                  );
                })}
            </dl>
          </aside>
        )}
      </div>

      <div className={styles.foot}>
        <span>
          {view.length} of {list.length} records
        </span>
        {moneyCol && view.length > 0 && (
          <span>
            Total {moneyCol.label.toLowerCase()}: <strong>{money(moneyTotal)}</strong>
          </span>
        )}
      </div>

      {form && <RecordDialog key={`${form}${sel?.id ?? ""}`} ent={ent} data={data} row={form === "edit" ? sel : null} onClose={() => setForm(null)} onSave={save} />}

      <Dialog open={confirmDel} onOpenChange={(_, d) => !d.open && setConfirmDel(false)}>
        <DialogSurface className={styles.dialogSm}>
          <DialogBody>
            <DialogTitle>Delete {ent.noun}?</DialogTitle>
            <DialogContent>{sel ? `${sel.id} will be removed. This can't be undone.` : ""}</DialogContent>
            <DialogActions>
              <DialogTrigger action="close">
                <Button>Cancel</Button>
              </DialogTrigger>
              <Button className={styles.danger} appearance="primary" onClick={remove}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

/* =====================================================================
   Finance workspace (dashboard)
   ===================================================================== */
const aging = (list: Row[]): number[] => {
  const b = [0, 0, 0, 0];
  const t = Date.parse(off(0));
  list.forEach((r) => {
    const days = Math.floor((t - Date.parse(String(r.due))) / 86400000);
    const i = days <= 0 ? 0 : days <= 30 ? 1 : days <= 60 ? 2 : 3;
    b[i] = (b[i] ?? 0) + num(r.amount);
  });
  return b;
};
const BUCKETS = ["Not due", "1–30 days", "31–60 days", "Over 60 days"];

const AgingPanel: React.FC<{ title: string; list: Row[]; tone: string }> = ({ title, list, tone }) => {
  const b = aging(list);
  const max = Math.max(...b, 1);
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <div className={styles.bars}>
        {b.map((v, i) => (
          <div key={BUCKETS[i]} className={styles.barRow}>
            <span className={styles.barLabel}>{BUCKETS[i]}</span>
            <span className={styles.barTrack}>
              <span className={`${styles.barFill} ${styles[tone]}`} style={{ width: `${(v / max) * 100}%` }} />
            </span>
            <span className={styles.barVal}>{money(v)}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const REVENUE = [58, 64, 61, 72, 69, 78];
const SPEND = [41, 44, 43, 49, 47, 52];

const Dashboard: React.FC<{ data: Data; go: (id: string) => void }> = ({ data, go }) => {
  const cash = sum(rows(data, "banks"), "balance");
  const arOpen = rows(data, "ar_invoices").filter((r) => r.status === "Posted");
  const apOpen = rows(data, "ap_invoices").filter((r) => r.status === "Posted");
  const overdueAr = arOpen.filter((r) => String(r.due) < off(0));
  const stock = rows(data, "inventory").reduce((t, r) => t + num(r.qty) * num(r.cost), 0);
  const openPo = rows(data, "pos").filter((r) => r.status === "Draft" || r.status === "Confirmed").length;

  const tiles: { label: string; value: string; note: string; to: string }[] = [
    { label: "Cash on hand", value: money(cash), note: `${rows(data, "banks").length} bank accounts`, to: "banks" },
    { label: "Receivables", value: money(sum(arOpen, "amount")), note: `${arOpen.length} open invoices`, to: "ar_invoices" },
    { label: "Payables", value: money(sum(apOpen, "amount")), note: `${apOpen.length} open invoices`, to: "ap_invoices" },
    { label: "Overdue receivables", value: money(sum(overdueAr, "amount")), note: `${overdueAr.length} invoices past due`, to: "ar_invoices" },
    { label: "Inventory value", value: money(stock), note: `${rows(data, "inventory").length} items`, to: "inventory" },
    { label: "Open purchase orders", value: String(openPo), note: "Draft or confirmed", to: "pos" },
  ];

  const todo: { label: string; n: number; to: string }[] = [
    { label: "Journals to post", n: rows(data, "journal").filter((r) => r.status === "Draft").length, to: "journal" },
    { label: "Vendor invoices to post", n: rows(data, "ap_invoices").filter((r) => r.status === "Draft").length, to: "ap_invoices" },
    { label: "Customer invoices to post", n: rows(data, "ar_invoices").filter((r) => r.status === "Draft").length, to: "ar_invoices" },
    { label: "Expense reports to approve", n: rows(data, "expenses").filter((r) => r.status === "Submitted").length, to: "expenses" },
    { label: "Purchase orders to confirm", n: rows(data, "pos").filter((r) => r.status === "Draft").length, to: "pos" },
    { label: "Items low on stock", n: rows(data, "inventory").filter((r) => num(r.qty) <= num(r.reorder)).length, to: "inventory" },
  ];

  const now = new Date();
  const months = REVENUE.map((_, i) => new Date(now.getFullYear(), now.getMonth() - (REVENUE.length - 1 - i), 1).toLocaleString("en-GB", { month: "short" }));
  const top = Math.max(...REVENUE, ...SPEND);

  return (
    <div className={styles.dash}>
      <div className={styles.tiles}>
        {tiles.map((t) => (
          <button key={t.label} type="button" className={styles.tile} onClick={() => go(t.to)}>
            <span className={styles.tileLabel}>{t.label}</span>
            <span className={styles.tileValue}>{t.value}</span>
            <span className={styles.tileNote}>{t.note}</span>
          </button>
        ))}
      </div>

      <div className={styles.dashGrid}>
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Revenue and expenses, last 6 months (₦ millions)</h3>
          <div className={styles.chart} role="img" aria-label={`Revenue ${REVENUE.join(", ")} and expenses ${SPEND.join(", ")} in millions of naira over the last six months`}>
            {months.map((m, i) => (
              <div key={m} className={styles.chartCol}>
                <div className={styles.chartBars}>
                  <span className={`${styles.chartBar} ${styles.revBar}`} style={{ height: `${((REVENUE[i] ?? 0) / top) * 100}%` }} title={`${m} revenue: ₦${REVENUE[i]}M`} />
                  <span className={`${styles.chartBar} ${styles.spendBar}`} style={{ height: `${((SPEND[i] ?? 0) / top) * 100}%` }} title={`${m} expenses: ₦${SPEND[i]}M`} />
                </div>
                <span className={styles.chartLabel}>{m}</span>
              </div>
            ))}
          </div>
          <div className={styles.legend}>
            <span>
              <i className={`${styles.dot} ${styles.revBar}`} />
              Revenue
            </span>
            <span>
              <i className={`${styles.dot} ${styles.spendBar}`} />
              Expenses
            </span>
          </div>
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Work waiting for you</h3>
          <ul className={styles.todo}>
            {todo.map((t) => (
              <li key={t.label}>
                <button type="button" className={styles.todoBtn} onClick={() => go(t.to)}>
                  <span>{t.label}</span>
                  <span className={`${styles.badge} ${t.n ? styles.badgeOn : ""}`}>{t.n}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <AgingPanel title="Receivables aging" list={arOpen} tone="fillBlue" />
        <AgingPanel title="Payables aging" list={apOpen} tone="fillSlate" />

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Cash by bank account</h3>
          <div className={styles.bars}>
            {rows(data, "banks").map((b) => (
              <div key={b.id} className={styles.barRow}>
                <span className={styles.barLabel}>{String(b.name)}</span>
                <span className={styles.barTrack}>
                  <span className={`${styles.barFill} ${styles.fillBlue}`} style={{ width: `${cash ? (num(b.balance) / cash) * 100 : 0}%` }} />
                </span>
                <span className={styles.barVal}>{money(num(b.balance))}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

/* =====================================================================
   Trial balance
   ===================================================================== */
const TrialBalance: React.FC<{ data: Data }> = ({ data }) => {
  const lines = [...rows(data, "coa")].sort((a, b) => a.id.localeCompare(b.id)).map((a) => {
    const bal = num(a.balance);
    const dn = debitNormal(String(a.type));
    const dr = dn ? Math.max(bal, 0) : Math.max(-bal, 0);
    const cr = dn ? Math.max(-bal, 0) : Math.max(bal, 0);
    return { id: a.id, name: String(a.name), type: String(a.type), dr, cr };
  });
  const dr = lines.reduce((t, l) => t + l.dr, 0);
  const cr = lines.reduce((t, l) => t + l.cr, 0);
  const ok = dr === cr;

  return (
    <>
      <div className={styles.filters}>
        <span className={`${styles.pill} ${ok ? styles.tone_green : styles.tone_red}`}>{ok ? "In balance" : `Out of balance by ${money(Math.abs(dr - cr))}`}</span>
      </div>
      <div className={styles.work}>
        <div className={styles.gridWrap}>
          <table className={styles.grid}>
            <thead>
              <tr>
                <th>Main account</th>
                <th>Name</th>
                <th>Account type</th>
                <th className={styles.right}>Debit</th>
                <th className={styles.right}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td className={styles.mono}>{l.id}</td>
                  <td>{l.name}</td>
                  <td>{l.type}</td>
                  <td className={styles.right}>{l.dr ? money(l.dr) : ""}</td>
                  <td className={styles.right}>{l.cr ? money(l.cr) : ""}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>Total</td>
                <td className={styles.right}>{money(dr)}</td>
                <td className={styles.right}>{money(cr)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
};

/* =====================================================================
   Income statement and balance sheet
   ===================================================================== */
const Statement: React.FC<{ data: Data; kind: "pnl" | "bs" }> = ({ data, kind }) => {
  const coa = [...rows(data, "coa")].sort((a, b) => a.id.localeCompare(b.id));
  const by = (t: string) => coa.filter((a) => a.type === t).map((a) => ({ id: a.id, name: String(a.name), amt: num(a.balance) }));
  const tot = (l: { amt: number }[]) => l.reduce((t, x) => t + x.amt, 0);
  const rev = by("Revenue");
  const exp = by("Expense");
  const profit = tot(rev) - tot(exp);
  const sections =
    kind === "pnl"
      ? [
          { title: "Revenue", lines: rev, total: tot(rev) },
          { title: "Expenses", lines: exp, total: tot(exp) },
        ]
      : [
          { title: "Assets", lines: by("Asset"), total: tot(by("Asset")) },
          { title: "Liabilities", lines: by("Liability"), total: tot(by("Liability")) },
          { title: "Equity", lines: [...by("Equity"), { id: "", name: "Profit for the period", amt: profit }], total: tot(by("Equity")) + profit },
        ];
  const balanced = kind === "bs" && (sections[0]?.total ?? 0) === (sections[1]?.total ?? 0) + (sections[2]?.total ?? 0);

  return (
    <>
      <div className={styles.filters}>
        {kind === "pnl" ? (
          <span className={`${styles.pill} ${profit >= 0 ? styles.tone_green : styles.tone_red}`}>{profit >= 0 ? "Net profit" : "Net loss"}: {money(Math.abs(profit))}</span>
        ) : (
          <span className={`${styles.pill} ${balanced ? styles.tone_green : styles.tone_red}`}>{balanced ? "Assets equal liabilities plus equity" : "Out of balance"}</span>
        )}
      </div>
      <div className={styles.work}>
        <div className={styles.gridWrap}>
          <table className={styles.grid}>
            <tbody>
              {sections.map((sec) => (
                <React.Fragment key={sec.title}>
                  <tr className={styles.secRow}>
                    <td colSpan={2}>{sec.title}</td>
                  </tr>
                  {sec.lines.map((l) => (
                    <tr key={l.id + l.name}>
                      <td>{l.id ? `${l.id} · ${l.name}` : l.name}</td>
                      <td className={styles.right}>{money(l.amt)}</td>
                    </tr>
                  ))}
                  <tr className={styles.totRow}>
                    <td>Total {sec.title.toLowerCase()}</td>
                    <td className={styles.right}>{money(sec.total)}</td>
                  </tr>
                </React.Fragment>
              ))}
              {kind === "pnl" && (
                <tr className={styles.totRow}>
                  <td>{profit >= 0 ? "Net profit" : "Net loss"}</td>
                  <td className={styles.right}>{money(profit)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/* =====================================================================
   Shell: navigation + the selected page
   ===================================================================== */
/** loading placeholder: shimmering rows while the first data load is in flight */
const SkeletonRows: React.FC<{ rows?: number; cols?: number }> = ({ rows = 8, cols = 5 }) => (
  <Skeleton aria-label="Loading" style={{ padding: 16, display: "grid", gap: 14 }}>
    <SkeletonItem size={16} style={{ width: 220 }} />
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 }}>
        {Array.from({ length: cols }, (_, j) => (
          <SkeletonItem key={j} size={16} />
        ))}
      </div>
    ))}
  </Skeleton>
);

const FinanceAndOperation: React.FC = () => {
  const [data, setData] = useState<Data>(seed);
  const [page, setPage] = useState("dashboard");
  const [navOpen, setNavOpen] = useState(false);
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const [navQ, setNavQ] = useState("");
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [conn, setConn] = useState<{ state: "loading" | "ok" | "off"; msg: string }>({ state: "loading", msg: "" });

  /** pages whose numbers also change when the named page changes (stock moves, receipts, shipments ...) */
  const reload = useCallback(async (only?: string): Promise<void> => {
    try {
      const c = await loadCtx();
      const ids = only ? Array.from(new Set([only, ...(DEPENDENTS[only] ?? [])])) : Object.keys(BINDINGS);
      const fresh: Data = {};
      const bad: string[] = [];
      await Promise.all(ids.map(async (id) => { const b = BINDINGS[id]; if (!b) return; try { fresh[id] = await b.load(c); } catch (e) { fresh[id] = []; bad.push(`${id}: ${errText(e)}`); } }));
      if (bad.length) say(bad[0] ?? "", true);
      setCtx(c);
      setData((d) => ({ ...d, ...fresh }));
      setConn({ state: "ok", msg: "" });
    } catch (e) {
      setConn({ state: "off", msg: errText(e) });
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  const live: Live = { ok: conn.state === "ok", ctx, reload };

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const say = (msg: string, err = false) => setToast({ msg, err });
  const go = (id: string) => {
    setPage(id);
    setNavOpen(false);
  };

  const modId = page.startsWith("mod:") ? page.slice(4) : null;
  const ent = ENTITIES.find((e) => e.id === page) ?? (ENTITIES[0] as Entity);
  const needle = navQ.trim().toLowerCase();
  const groups = ENTITIES.reduce<{ name: string; items: Entity[] }[]>((acc, e) => {
    if (needle && !`${e.title} ${e.group}`.toLowerCase().includes(needle)) return acc;
    const g = acc.find((x) => x.name === e.group);
    if (g) g.items.push(e);
    else acc.push({ name: e.group, items: [e] });
    return acc;
  }, []).sort((a, b) => GROUP_ORDER.indexOf(a.name) - GROUP_ORDER.indexOf(b.name));

  return (
    <div className={styles.gU1Tf}>
      <div className={styles.jUsfu}>
        <div className={styles.QIl4y}>
    <FluentProvider theme={webLightTheme} className={styles.root}>
      <aside className={`${styles.side} ${navOpen ? styles.sideOpen : ""}`} aria-label="Finance and operations navigation">
        <div className={styles.company}>
          <span className={styles.companyMark}>
            <Money20Regular />
          </span>
          <div>
            <div className={styles.companyName}>Finance and Operations</div>
            <div className={styles.companySub}>Cilpron Hospitality · NGN</div>
          </div>
        </div>
        <SearchBox className={styles.navSearch} placeholder="Search for a page" value={navQ} onChange={(_, d) => setNavQ(d.value)} />
        <nav className={styles.nav}>
          {groups.map((g) => {
            const collapsed = !needle && !(opened[g.name] ?? g.name === ent.group);
            const single = g.items.length === 1 && g.name === "Workspaces";
            return (
              <div key={g.name} className={styles.navGroup}>
                {!single && (
                  <button type="button" className={styles.navHead} aria-expanded={!collapsed} onClick={() => setOpened({ ...opened, [g.name]: !!collapsed })}>
                    <ChevronDown12Regular style={{ transform: collapsed ? "rotate(-90deg)" : "none" }} />
                    {g.name}
                  </button>
                )}
                {single && <div className={styles.navLabel}>{g.name}</div>}
                {!collapsed &&
                  g.items.map((e) => (
                    <button key={e.id} type="button" className={`${styles.navItem} ${page === e.id ? styles.navActive : ""}`} aria-current={page === e.id ? "page" : undefined} onClick={() => go(e.id)}>
                      {e.icon}
                      {e.title}
                    </button>
                  ))}
              </div>
            );
          })}
          {(!needle || "all backend modules".includes(needle) || MODULES.some((m) => m[1].toLowerCase().includes(needle))) && (
            <div className={styles.navGroup}>
              <button type="button" className={styles.navHead} aria-expanded={!!(needle || opened["All backend modules"] || modId)} onClick={() => setOpened({ ...opened, "All backend modules": !opened["All backend modules"] })}>
                <ChevronDown12Regular style={{ transform: needle || opened["All backend modules"] || modId ? "none" : "rotate(-90deg)" }} />
                All backend modules
              </button>
              {(needle || opened["All backend modules"] || modId) &&
                MODULES.filter((m) => !needle || m[1].toLowerCase().includes(needle) || "all backend modules".includes(needle)).map((m) => (
                  <button key={m[0]} type="button" className={`${styles.navItem} ${page === "mod:" + m[0] ? styles.navActive : ""}`} aria-current={page === "mod:" + m[0] ? "page" : undefined} onClick={() => go("mod:" + m[0])}>
                    <Table20Regular />
                    {m[1]}
                  </button>
                ))}
            </div>
          )}
          {groups.length === 0 && !MODULES.some((m) => m[1].toLowerCase().includes(needle)) && <div className={styles.navEmpty}>No pages match.</div>}
        </nav>
      </aside>
      {navOpen && <div className={styles.scrim} onClick={() => setNavOpen(false)} aria-hidden="true" />}

      <main className={styles.main}>
        <header className={styles.head}>
          <Button className={styles.navToggle} appearance="subtle" icon={<Navigation20Regular />} aria-label="Toggle navigation" onClick={() => setNavOpen(!navOpen)} />
          <div>
            <div className={styles.crumb}>
              Finance and Operations › {modId ? "All backend modules" : ent.group} › {modId ? (MODULES.find((m) => m[0] === modId)?.[1] ?? modId) : ent.title}
            </div>
            <h1 className={styles.title}>{modId ? (MODULES.find((m) => m[0] === modId)?.[1] ?? modId) : ent.title}</h1>
            <p className={styles.sub}>{modId ? "Every resource this backend module exposes - browse, create, edit and run its actions." : ent.subtitle}</p>
          </div>
        </header>

        {conn.state === "loading" && <div className={`${gp.banner} ${gp.bannerOff}`}>Connecting to the F&amp;O API…</div>}
        {conn.state === "off" && (
          <div className={`${gp.banner} ${gp.bannerOff}`} role="alert">
            <span>
              Cannot load from the F&amp;O API ({BASE}): {conn.msg}
              {!getToken() ? " No sign-in token was found." : ""} The operations pages stay empty until it connects.
            </span>
            <Button size="small" onClick={() => void reload()}>Retry</Button>
          </div>
        )}
        {conn.state === "ok" && !modId && ent.page === "grid" && (
          <div className={`${gp.banner} ${BINDINGS[ent.id] ? gp.bannerOk : gp.bannerOff}`}>
            {BINDINGS[ent.id] ? (["coa", "journal", "ar_invoices", "ap_invoices", "banks", "taxes", "assets", "budgets", "expenses", "periods"].includes(ent.id) ? "Live data from the accounting (Cilpronacc) API." : "Live data from the F&O API.") : "This page is not connected to an API yet, so it has no data."}
          </div>
        )}

        {modId && <GenericPage key={modId} module={modId} say={say} />}
        {!modId && conn.state === "loading" && <SkeletonRows />}
        {!modId && conn.state !== "loading" && ent.page === "dash" && <Dashboard data={data} go={go} />}
        {!modId && conn.state !== "loading" && ent.page === "trial" && <TrialBalance data={data} />}
        {!modId && conn.state !== "loading" && (ent.page === "pnl" || ent.page === "bs") && <Statement data={data} kind={ent.page} />}
        {!modId && conn.state !== "loading" && ent.page === "grid" && <EntityPage key={ent.id} ent={ent} data={data} setData={setData} say={say} go={go} live={live} />}
      </main>

      {toast && (
        <div className={`${styles.toast} ${toast.err ? styles.toastErr : ""}`} role="status" aria-live="polite">
          {toast.msg}
        </div>
      )}
    </FluentProvider>
        </div>
      </div>
    </div>
  );
};

export default FinanceAndOperation;